import _ from "lodash";
import store from "services/redux/store";
import { setData } from "services/redux/data/actions";
import { dataConstants } from "services/redux/data/constants";
import FetchCache from "utils/fetch/cached/FetchCache";
import FetchEmployeeErosFavouritesBookings from 'utils/fetch/FetchEmployeeErosFavouritesBookings'
import Translate from 'utils/translate'
import { EmployeePresentStatus } from 'utils/constants/EmployeePresentStatus'
import { constantsActive } from 'utils/constants/constants'
import { timeslotKindConstants } from 'utils/constants/timeslot-kind-constants'
import HelperArray from 'utils/helper-array'
import HelperDate, { dateFormat } from 'utils/helper-date'

export default class FetchCachedOtherEmployeeBookings {
	
	static logName = 'FetchCachedFavourites';
	static employees = {};
	static values = {};
	static cacheUpdatedAt = {};
	static reloadInterval = null;
	
	static reset = () => {
		this.cacheUpdatedAt = {};
		this.values = {};
		
		store.dispatch( setData( dataConstants.EMPLOYEES_BOOKINGS, this.values ) );
	}
	
	static construct = async ( bookingDate  = HelperDate.date() ) => {
		
		if( this.reloadInterval === null ) {
			this.reloadInterval = setInterval( this.reloadCurrentBookings, 60*1000 );
		}
		
		const bookingDateYmd = bookingDate.format( dateFormat.DATE );
		
		if( _.get( this.cacheUpdatedAt, bookingDateYmd, null ) !== null ) {
			return;
		}
		
		this.cacheUpdatedAt[ bookingDateYmd ] = {
			identifier: 'other_employee_bookings_'+ bookingDateYmd,
			tables: [ 'other_employee_bookings_'+ bookingDateYmd ],
			onExpire: () => {
				return this.reload({
					bookingDate: bookingDate
				});
			},
			updated_at_max_age: ( 5 * 60 ) // updated_at needs to be refresh at most each 5 minutes for this table
		}
		
		await FetchCache.register( this.cacheUpdatedAt[ bookingDateYmd ], () => {
		} );
	}

	static fetchAll = ( { callbackSuccess = null, callbackError = null, callbackStart = null, bookingDate  = HelperDate.date() } = {} ) => {
		const bookingDateYmd = bookingDate.format( dateFormat.DATE );
		
		this.construct( bookingDate ).then( () =>
			{
			return (
				FetchCache.sniff( this.cacheUpdatedAt[ bookingDateYmd ], {
					onFresh: () => {
						if( _.isEmpty( this.values[ bookingDateYmd ] ) ) {
							this.reload( {callbackSuccess, callbackError, callbackStart, bookingDate } );
						}else{
							if( callbackSuccess !== null && _.isFunction( callbackSuccess ) ) {
								callbackSuccess( this.values[ bookingDateYmd ] )
							}
						}
					},
					onExpired: () => {
						this.reload( {callbackSuccess, callbackError, callbackStart, bookingDate } );
					}
				})
			);
		}
		);
	}

	static reload = ( { callbackSuccess = null, callbackError = null, callbackStart = null, bookingDate } = {} ) => {
		const bookingDateYmd = bookingDate?.format( dateFormat.DATE );
		
		this.construct().then( () =>
			{
			return (
				FetchEmployeeErosFavouritesBookings.fetchAll({
					variables: {
						booking_date: [ bookingDateYmd ],
						scanned_in_at: HelperDate.date().format( dateFormat.DATE ) +'_after'
					},
					callbackStart: callbackStart,
					callbackError: callbackError,
					callbackSuccess: ( employees ) => {
						this.employees = {
							...this.employees,
							[ bookingDateYmd ]: {
								...employees
							}
						};

						const results = this.getCurrentBookings( bookingDate, employees );

						// eliminate all the employees without bookings, fetch only the current booking and set a status and location based on that all
						this.process( bookingDateYmd, results );

						if( _.isFunction( callbackSuccess ) ){
							callbackSuccess( results );
						}

						// !! Bookings of today are NOT stored on the users phone, we only use this cache for proper retrieval
						// FetchCache.storeData( this.cacheUpdatedAt, results, true );
					}
				})
			);
		}
		)
	}
	
	static reloadCurrentBookings = () => {
		let dates = Object.keys( this.values );
		
		for( const bookingDate of dates ) {
			
			let results = this.getCurrentBookings( HelperDate.date( bookingDate, dateFormat.DATE ), _.get( this.employees, bookingDate, {} ) );
			let changed = false;
			
			// check for new or changed entries
			for( const employee_id in results ) {
				if( !_.has( this.values[ bookingDate ], employee_id ) && !_.isEqual( this.values[ bookingDate ][ employee_id ], results[ employee_id ] ) ){
					changed = true;
				}
			}
			
			// check for removed entries
			for( const employee_id in this.values[ bookingDate ] ) {
				if( !_.has( results, employee_id ) ){
					changed = true;
				}
			}
			
			if( changed ){
				this.process( bookingDate, results );
			}
		}
	}
	
	static createLocationName = ( booking ) => {
		const building = Translate.field( _.get( store.getState().data[ dataConstants.BUILDINGS ], booking.building?.guid, {} ), 'title' );
		const floor = Translate.field( _.get( store.getState().data[ dataConstants.FLOORS ], booking.floor?.guid, {} ), 'title' );
		const room = Translate.field( _.get( store.getState().data[ dataConstants.ROOMS ], booking.room?.guid, {} ), 'title' );
		
		return _.reject( [ building, floor, room ], _.isEmpty).join( ' - ' );
	}
	
	static getCurrentBookings = ( bookingDate, employees ) => {
		const currentTime = HelperDate.date();
		
		const isToday = HelperDate.fn.isToday( bookingDate )
		
		let results = {};
		
		const allowedTimeslotKinds = [ timeslotKindConstants.BOOKING, timeslotKindConstants.MEETING_ROOM, timeslotKindConstants.LUNCH, timeslotKindConstants.CUSTOM ];
		
		_.forEach( employees, ( employee ) => {
			// Filter bookings which are 'not' allowed
			const bookings = _.filter( _.get( employee, 'bookings', [] ), o => {
				return _.includes( allowedTimeslotKinds, o?.kind ) && o.booking_date === bookingDate.format( dateFormat.DATE )
			});
			
			switch( true ) {
				case !isToday && _.size( bookings ) > 0:
					const bookingLocations = [];
					
					_.forEach( bookings, ( tmp_booking ) => {
						const locationName = this.createLocationName( tmp_booking );
						
						if( !_.includes( bookingLocations, locationName ) ) {
							bookingLocations.push( locationName );
						}
					});
					
					results[ employee.guid ] = {
						guid: employee.guid,
						location: HelperArray.naturalJoin( bookingLocations ),
						status: EmployeePresentStatus.UNKNOWN
					};
					break;
					
				case isToday && _.size( bookings ) > 0:
					let booking = null;
					
					// Check now
					_.forEach( allowedTimeslotKinds, ( timeslot_kind ) => {
						if( booking === null ) {
							_.forEach( bookings, ( tmp_booking ) => {
								const timeslotStart = HelperDate.date( tmp_booking.timeslot.timeslot_start, dateFormat.FULL_TIME );
								const timeslotEnd = HelperDate.date( tmp_booking.timeslot.timeslot_end, dateFormat.FULL_TIME );
								
								if( tmp_booking.timeslot?.kind === timeslot_kind && currentTime.isBetween( timeslotStart, timeslotEnd ) ) {
									booking = tmp_booking;
									return false;
								}
							});
						}
					});
					
					// Check before
					if( booking === null ) {
						_.forEach( [ timeslotKindConstants.BOOKING ], ( timeslot_kind ) => {
							if( booking === null ) {
								_.forEach( bookings, ( tmp_booking ) => {
									const timeslotEnd = HelperDate.date( tmp_booking.timeslot.timeslot_end, dateFormat.FULL_TIME );
									
									if( tmp_booking.timeslot?.kind === timeslot_kind && currentTime.isBefore( timeslotEnd ) ) {
										booking = tmp_booking;
										return false;
									}
								});
							}
						});
					}
					
					// Check after
					if( booking === null ) {
						_.forEach( [ timeslotKindConstants.BOOKING ], ( timeslot_kind ) => {
							if( booking === null ) {
								//_.forEach( bookings, ( tmp_booking ) => {
								_.forEach( _.orderBy( bookings, 'timeslot.timeslot_end', 'desc' ), ( tmp_booking ) => {
									const timeslotEnd = HelperDate.date( tmp_booking.timeslot.timeslot_end, dateFormat.FULL_TIME );
									
									if( tmp_booking.timeslot?.kind === timeslot_kind && currentTime.isAfter( timeslotEnd ) ) {
										booking = tmp_booking;
										return false;
									}
								});
							}
						});
					}
					
					if( booking !== null ) {
						let status = null;
						
						switch( booking.timeslot?.kind ) {
							case timeslotKindConstants.MEETING_ROOM:
								status = EmployeePresentStatus.IN_MEETING;
								break;
							
							case timeslotKindConstants.LUNCH:
								status = EmployeePresentStatus.HAVING_LUNCH;
								break;
							
							default:
								// check the scans
								status = EmployeePresentStatus.NOT_YET_PRESENT;
								
								const scans = _.get( employee, 'scans', [] );
								
								if( _.size( scans ) > 0 ) {
									_.forEach( scans, scan => {
										const scannedInAt = _.get( scan, 'scanned_in_at', null );
										const scannedOutAt = _.get( scan, 'scanned_out_at', null );
										
										if( scannedInAt !== null && scannedOutAt === null ) {
											status = EmployeePresentStatus.PRESENT;
										}else if( scannedOutAt !== null ) {
											status = EmployeePresentStatus.LEFT;
										}
									});
								}
								break;
						}
						
						results[ employee.guid ] = {
							guid: employee.guid,
							location: this.createLocationName( booking ),
							status: status
						};
					}
					break;
					
				default:
					const workdays = _.get( employee, 'workdays', [] );
					const vacationModeActive = _.get( employee, 'vacation_mode_active', constantsActive.NO ) === constantsActive.YES
					
					// Check the workday
					if( !vacationModeActive && !_.isEmpty( workdays ) ) {
						const workday = _.find( workdays, { workday_day: bookingDate.isoWeekday() } )
						
						if( !_.isEmpty( workday ) ) {
							const workdayStart = HelperDate.date( workday.workday_start, dateFormat.FULL_TIME );
							const workdayEnd = HelperDate.date( workday.workday_end, dateFormat.FULL_TIME );
							
							results[ employee.guid ] = {
								guid: employee.guid,
								location: null,
								status: ( !isToday || currentTime.isBetween( workdayStart, workdayEnd )) ? EmployeePresentStatus.WORK_FROM_HOME : EmployeePresentStatus.WORK_FROM_HOME_2
							};
						}
					}
			}
		});
		
		return results;
	}

	static process = ( bookingDateYmd, employee_bookings ) => {
		this.values[ bookingDateYmd ] = {
			...employee_bookings
		};
		
		// trigger an actual change (Oh javascript you bastard)
		this.values = {
			...this.values
		};
		
		store.dispatch( setData( dataConstants.EMPLOYEES_BOOKINGS, this.values ) );
	}
}
